'use client';

import { useState } from 'react';
import Link from 'next/link';
import CategoryCard from '@/components/blogHome/CategoryCard';
import dynamic from 'next/dynamic';
import { ArrowRightIcon } from '@/assets';

import {
  BlogDocument,
  BlogCategoryDocument,
} from '../../../../../prismicio-types';

// Dynamically import StarfieldBackground with loading deferred (matching HeroCtaWithLogos default)
const StarfieldBackground = dynamic(
  () => import('@/components/StarfieldBackground'),
  { ssr: false },
);

interface Props {
  category: BlogCategoryDocument;
  initialPosts: BlogDocument[];
  initialHasMore: boolean;
  categorySlug: string;
}

export default function CategoryPageClient({
  category,
  initialPosts,
  initialHasMore,
  categorySlug,
}: Props) {
  const [posts, setPosts] = useState<BlogDocument[]>(initialPosts);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialHasMore);

  const loadMore = () => {
    if (loading || !hasMore) {
      return;
    }
    setLoading(true);

    fetch(`/api/blog/category/${categorySlug}?page=${page + 1}`)
      .then((response) => response.json())
      .then((data) => {
        if (data.results && data.results.length > 0) {
          setPosts((prev) => [...prev, ...data.results]);
          setPage((prev) => prev + 1);
          setHasMore(data.hasMore);
        } else {
          setHasMore(false);
        }
      })
      .catch((error) => {
        console.error('Error loading more posts:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="relative min-h-screen text-white overflow-hidden">
      {/* Background gradient - always visible, prevents white flash */}
      <div
        className="absolute inset-0 w-full h-full"
        style={{
          background: 'linear-gradient(180deg, #0E1010 0%, #0F423A 100%)',
          zIndex: -2,
        }}
      />

      {/* Starfield animation - loaded after gradient (matching HeroCtaWithLogos default) */}
      <StarfieldBackground />

      <div className="relative z-10 overflow-x-hidden">
        {/* Category Header with Breadcrumb */}
        <div className="pt-[8rem] px-4 sm:px-10 lg:px-[174px] pb-8">
          <div className="max-w-[1300px] mx-auto">
            {/* Breadcrumb directly above title */}
            <div className="mb-4">
              {/* Custom left-aligned breadcrumb */}
              <nav className="flex justify-start" aria-label="Breadcrumb">
                <ol className="flex items-center space-x-2">
                  <li>
                    <Link href="/">
                      <span
                        className="text-[#05F273] font-light text-base leading-[150%]"
                        style={{ fontFamily: 'Inter' }}
                      >
                        Home
                      </span>
                    </Link>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <span className="text-[#05F273] mx-2">
                        <ArrowRightIcon fill="#05F273" />
                      </span>
                      <Link href="/resources-center">
                        <span
                          className="text-[#05F273] font-light text-base leading-[150%]"
                          style={{ fontFamily: 'Inter' }}
                        >
                          Resources
                        </span>
                      </Link>
                    </div>
                  </li>
                  <li>
                    <div className="flex items-center">
                      <span className="text-[#05F273] mx-2">
                        <ArrowRightIcon fill="#05F273" />
                      </span>
                      <span className="text-[#05F273] font-bold">
                        {category.data.name}
                      </span>
                    </div>
                  </li>
                </ol>
              </nav>
            </div>

            {/* Category Title with Return Arrow */}
            <div className="flex items-center gap-4">
              {/* Return Arrow */}
              <button
                onClick={() => window.history.back()}
                className="flex items-center justify-center w-10 h-10 hover:bg-white/10 rounded-full transition-colors"
                aria-label="Go back"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="23"
                  viewBox="0 0 22 23"
                  fill="none"
                >
                  <path
                    d="M22 10.0625L5.26625 10.0625L12.9525 2.02688L11 1.42652e-06L-5.02681e-07 11.5L11 23L12.9388 20.9731L5.26625 12.9375L22 12.9375L22 10.0625Z"
                    fill="#05F273"
                  />
                </svg>
              </button>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                {category.data.name}
              </h1>
            </div>
          </div>
        </div>

        {/* Posts Grid */}
        <div className="bg-black w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] px-4 sm:px-10 lg:px-[174px] py-16">
          <div className="max-w-[1300px] mx-auto">
            {posts.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                  {posts.map((post) => {
                    return (
                      <CategoryCard
                        key={post.id}
                        post={post}
                        category={category.data.name || categorySlug}
                      />
                    );
                  })}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="flex justify-center w-full mt-8">
                    <button
                      className={`secondary-button ${loading ? 'opacity-60 pointer-events-none' : ''}`}
                      onClick={loadMore}
                      disabled={loading}
                      aria-label="Load more articles"
                    >
                      {loading ? 'Loading…' : 'Load more'}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16">
                <h2 className="text-2xl font-semibold text-white mb-4">
                  No articles found
                </h2>
                <p className="text-gray-400">
                  There are no articles in this category yet. Check back soon
                  for new content!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
